import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";

import { Modal, OpenIcon } from "../../components/ui";
import { safeArtifactDownloadUrl } from "../../lib/artifactDownload";
import type { AgentStoredDocument } from "../api/e2e";
import { useAgentDocumentLink } from "../api/e2e";
import { documentPreviewLocator, focusedPreview } from "./XLMDocumentEvidence";

type PreviewState =
  | { kind: "loading" }
  | { kind: "ready"; url: string; blob: Blob; ownedUrls: string[] }
  | { kind: "download"; blob: Blob }
  | { kind: "external"; url: string }
  | { kind: "error"; message: string };

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export default function AgentSummaryDocumentPreview({
  companyId,
  document,
  onClose,
}: {
  companyId: string;
  document: AgentStoredDocument;
  onClose: () => void;
}) {
  const { mutateAsync: createDocumentLink } = useAgentDocumentLink();
  const [preview, setPreview] = useState<PreviewState>({ kind: "loading" });

  useEffect(() => {
    let active = true;
    let ownedUrls: string[] = [];
    const load = async () => {
      try {
        const resolved = await createDocumentLink({
          companyId,
          documentId: document.id,
        });
        if (!active) return;
        let response: Response;
        if (resolved.preview_token === null) {
          const originalUrl = safeArtifactDownloadUrl(
            resolved.url,
            window.location.origin,
          );
          if (!import.meta.env.DEV) {
            setPreview({ kind: "external", url: originalUrl });
            return;
          }
          response = await fetch("/__document-preview", {
            method: "POST",
            body: originalUrl,
            credentials: "omit",
            referrerPolicy: "no-referrer",
          });
        } else {
          response = await fetch(
            `/api/v1/document-capabilities/${encodeURIComponent(companyId)}/${encodeURIComponent(document.id)}`,
            {
              credentials: "omit",
              referrerPolicy: "no-referrer",
              headers: { "X-Document-Capability": resolved.preview_token },
            },
          );
        }
        if (!response.ok) throw new Error("Document preview failed.");
        const blob = await response.blob();
        const rendered = await focusedPreview(
          blob,
          document.filename,
          documentPreviewLocator(document),
          null,
          [],
        );
        if (!active) {
          for (const url of rendered?.ownedUrls ?? []) URL.revokeObjectURL(url);
          return;
        }
        if (!rendered) {
          setPreview({ kind: "download", blob });
          return;
        }
        ownedUrls = rendered.ownedUrls;
        setPreview({ kind: "ready", blob, ...rendered });
      } catch (error) {
        if (!active) return;
        const message =
          error instanceof Error
            ? error.message
            : "Document preview could not be opened.";
        setPreview({ kind: "error", message });
        toast.error(message);
      }
    };
    void load();
    return () => {
      active = false;
      for (const url of ownedUrls) URL.revokeObjectURL(url);
    };
  }, [companyId, createDocumentLink, document]);

  return (
    <Modal
      size="preview"
      className="xlm-document-modal"
      ariaLabelledBy="summary-document-modal-title"
      onClose={onClose}
    >
      <header>
        <div>
          <span className="eyebrow">Referenced document</span>
          <strong id="summary-document-modal-title">{document.name}</strong>
          <small>
            {document.filename}
            {document.release_date ? ` · ${document.release_date}` : ""}
          </small>
          <small>{document.description}</small>
        </div>
        <div className="xlm-document-modal__actions">
          {document.source_url && (
            <a href={document.source_url} target="_blank" rel="noreferrer">
              Open source URL <OpenIcon />
            </a>
          )}
          {(preview.kind === "ready" || preview.kind === "external") && (
            <button
              type="button"
              onClick={() => window.open(preview.url, "_blank", "noopener")}
            >
              Open separately <OpenIcon />
            </button>
          )}
          {(preview.kind === "ready" || preview.kind === "download") && (
            <button
              type="button"
              onClick={() => saveBlob(preview.blob, document.filename)}
            >
              Download
            </button>
          )}
          <button type="button" aria-label="Close document" onClick={onClose}>
            ×
          </button>
        </div>
      </header>
      <div className="xlm-document-modal__body" aria-live="polite">
        {preview.kind === "loading" && (
          <div className="xlm-document-preview__state" role="status">
            Loading document preview…
          </div>
        )}
        {preview.kind === "error" && (
          <div className="xlm-document-preview__state" role="alert">
            {preview.message}
          </div>
        )}
        {preview.kind === "download" && (
          <div className="xlm-document-preview__state">
            <strong>This format has no safe inline browser renderer.</strong>
            <p>You can download the exact original from this preview.</p>
          </div>
        )}
        {preview.kind === "external" && (
          <div className="xlm-document-preview__state">
            <strong>The inline preview service is unavailable.</strong>
            <p>Open the original document in a separate tab.</p>
          </div>
        )}
        {preview.kind === "ready" && (
          <iframe
            className="xlm-document-preview__frame"
            src={preview.url}
            title={`Preview of ${document.name}, file ${document.filename}`}
          />
        )}
      </div>
    </Modal>
  );
}
